import { defineMessages } from 'react-intl';

export const clinicianTrialEndedModalMessages = defineMessages({
  // ── Titles ─────────────────────────────────────────────────────────────
  title: {
    defaultMessage: 'Your complimentary access to Evidence has ended',
    id: 'rgKkg4+f5k',
    description:
      'Main heading on the Clinician trial-ended modal (solo Clinician plan, returning to Scribe Plus)',
  },
  titlePractice: {
    defaultMessage: "Your team's complimentary access to Evidence has ended",
    id: 'H7Iy0ePwTL',
    description:
      'Main heading on the Practice trial-ended modal (Practice admin / non-admin returning to Scribe Team)',
  },
  titleNonAdmin: {
    defaultMessage: 'Your complimentary access to Evidence has ended',
    id: 'YeOd6KRMs5',
    description:
      'Main heading on the Practice trial-ended modal for non-admin team members; phrased per-user (vs the team-level admin variant)',
  },

  // ── Subtitles / bodies ────────────────────────────────────────────────
  bodySoloClinician: {
    defaultMessage: 'Unlock in-session clinical answers with preferred source control.',
    id: 'QbJrj9Cp1d',
    description:
      'Subtitle on the Clinician trial-ended modal for solo users; rendered when the Clinician price cannot be resolved (no products loaded, no matching cadence). Region/cadence-aware variant is bodySoloClinicianWithPrice.',
  },
  bodySoloClinicianWithPrice: {
    defaultMessage:
      'Unlock in-session clinical answers with preferred source control, bringing your plan to {price}/month.',
    id: 'NMrOHEt5mk',
    description:
      "Region/cadence-aware subtitle on the Clinician trial-ended modal for solo users. {price} is the user's region currency-formatted price (resolved from Stripe via the products API): monthly subscribers see the Clinician monthly price (e.g. $120 US, $150 AU); annual subscribers see the Clinician annual per-month price (e.g. $85 US, $110 AU). Falls back to bodySoloClinician when the price is unavailable.",
  },
  bodySoloClinicianNoInSession: {
    defaultMessage: 'Keep Evidence with preferred source control.',
    id: 'QVJTmZz6Ux',
    description:
      'Subtitle on the Clinician trial-ended modal for solo users where in-session Evidence is hidden by the geo/access gate (UK region, Japan, EEA countries, or no in-session access); rendered when the Clinician price cannot be resolved.',
  },
  bodySoloClinicianNoInSessionWithPrice: {
    defaultMessage:
      'Keep Evidence with preferred source control, bringing your plan to {price}/month.',
    id: 'sBb+wNhpzJ',
    description:
      "Region/cadence-aware subtitle on the Clinician trial-ended modal for solo users where in-session Evidence is hidden by the geo/access gate (UK region, Japan, EEA countries, or no in-session access). {price} is the user's region currency-formatted price (resolved from Stripe via the products API): monthly subscribers see the Clinician monthly price (e.g. $120 US, $150 AU); annual subscribers see the Clinician annual per-month price (e.g. $85 US, $110 AU). Falls back to bodySoloClinicianNoInSession when the price is unavailable.",
  },
  bodyPracticeAdmin: {
    defaultMessage:
      'Upgrade to the new Practice plan for your team to access clinical answers in sessions.',
    id: 'cJWkWlq/0J',
    description:
      'Subtitle on the Practice trial-ended modal for team admins; pitches the team-wide upgrade',
  },
  bodyPracticeAdminNoInSession: {
    defaultMessage:
      'Upgrade to the new Practice plan for your team to keep Evidence with preferred source control.',
    id: 'FR/GoRhB0k',
    description:
      'Subtitle on the Practice trial-ended modal for team admins where in-session Evidence is hidden by the geo/access gate (UK region, Japan, EEA countries, or no in-session access); pitches the team-wide upgrade without in-session wording',
  },
  bodyNonAdmin: {
    defaultMessage:
      'Request to keep Evidence with your admin for clinical answers in sessions.',
    id: 'FsGF8w+NKg',
    description:
      'Subtitle on the Practice trial-ended modal for non-admin team members; tells them to ask their admin to upgrade',
  },
  bodyNonAdminNoInSession: {
    defaultMessage: 'Request to keep Evidence with your admin.',
    id: 'ZhaFAaEQ7r',
    description:
      'Subtitle on the Practice trial-ended modal for non-admin team members where in-session Evidence is hidden by the geo/access gate (UK region, Japan, EEA countries, or no in-session access); tells them to ask their admin to upgrade without in-session wording',
  },

  // ── Tier names ─────────────────────────────────────────────────────────
  scribePlusPlanName: {
    defaultMessage: 'Scribe Plus',
    id: 'yqdTk4wou7',
    description:
      'Display name for Scribe Plus when describing the plan users return to after a Clinician trial ends',
  },
  scribeTeamPlanName: {
    defaultMessage: 'Scribe Team',
    id: 'r2TvNUxuk0',
    description:
      'Display name for Scribe Team when describing the plan users return to after a Practice trial ends',
  },
  backOnPlanToast: {
    defaultMessage: "You're back on {planName}",
    id: 'vqXn/EVkSY',
    description:
      'Info toast shown when the user closes the trial-ended modal without upgrading (remain CTA or dismiss)',
  },

  // ── Segmented control tabs ────────────────────────────────────────────
  tabClinician: {
    defaultMessage: 'Clinician',
    id: 'Y0Lag4Keau',
    description: 'Segmented control tab label for Clinician recap content',
  },
  tabPractice: {
    defaultMessage: 'Practice',
    id: 'wQGmMHe0ZJ',
    description:
      'Segmented control tab label for Practice recap content on the trial-ended modal',
  },
  tabScribePlus: {
    defaultMessage: 'Scribe Plus',
    id: 'Wo5tmffF6R',
    description: 'Segmented control tab label for Scribe Plus retained features',
  },
  tabScribeTeam: {
    defaultMessage: 'Scribe Team',
    id: 'jOMbW6tbut',
    description:
      'Segmented control tab label for Scribe Team retained features when the ended trial was Practice',
  },

  // ── Section labels ─────────────────────────────────────────────────────
  whatYouveDone: {
    defaultMessage: "What you've done:",
    id: 'pTqTIrUt9E',
    description: 'Section label above the Clinician trial usage recap list',
  },

  // ── "Current" badge on the segmented control tab the user is on now ──
  currentTierBadge: {
    defaultMessage: 'Current',
    id: 'DPk+PA04rc',
    description:
      'Small blue badge next to the Scribe Plus / Scribe Team segmented control tab indicating that is the plan the user currently belongs to',
  },

  // ── Feature-list section header (shared across all three cohorts) ───
  featuresHeader: {
    defaultMessage: 'Everything on your current plan, plus:',
    id: '681tuTLD9d',
    description:
      'Section header above the upgrade-pitch feature list on the trial-ended modal; shared across the Clinician, Practice admin, and Practice non-admin cohorts',
  },

  // ── Feature row labels ───────────────────────────────────────────────
  featureAnswersInPatientSessions: {
    defaultMessage: 'Answers in patient sessions',
    id: 'lvWF5pK09l',
    description:
      'Feature row label on the trial-ended modal Clinician/Practice tab; describes higher Evidence answer limits inside patient sessions',
  },
  featureSetPreferredSources: {
    defaultMessage: 'Set preferred sources',
    id: 'xqwZcj2OaH',
    description:
      'Feature row label on the trial-ended modal Clinician/Practice tab; describes the ability to pick preferred evidence sources by region/specialty',
  },
  featureTeamGuidelines: {
    defaultMessage: 'Team guidelines',
    id: 'IocttVK/1U',
    description:
      'Feature row label on the trial-ended modal Practice tab (admins only); describes shared team guideline documents',
  },

  // ── Usage badge ──────────────────────────────────────────────────────
  usageBadge: {
    defaultMessage: 'Used {count}x',
    id: '4erlQaSohQ',
    description:
      'Pill badge on the right of the "Answers in patient sessions" row showing how many times the user used Evidence answers during the trial (always "Used Nx" — no pluralization)',
  },

  // ── Primary CTAs ───────────────────────────────────────────────────────
  upgradeToClinicianCta: {
    defaultMessage: 'Keep Evidence',
    id: 'vLY12uVIoM',
    description:
      'Primary CTA on the trial-ended modal for solo Clinician users; opens the in-app Clinician upgrade flow',
  },
  upgradeToPracticeCta: {
    defaultMessage: 'Keep Evidence',
    id: 'kSsTXpDHKe',
    description:
      'Primary CTA on the trial-ended modal for Practice admins; opens the in-app Practice upgrade flow',
  },
  requestToUpgradeCta: {
    defaultMessage: 'Request to keep Evidence',
    id: 'ZBBWxCHt/s',
    description:
      'Primary CTA on the trial-ended modal for non-admin team members; opens the team upgrade request modal so the user can ask their admin to upgrade',
  },
  requestToUpgradeAlreadySentTooltip: {
    defaultMessage: "You've already sent an upgrade request to your admin",
    id: 'ODBkw0w1D4',
    description:
      'Tooltip on the trial-ended modal non-admin CTA when the user has already requested an upgrade; button stays on "Request to keep Evidence" but is disabled',
  },

  // ── Secondary "Remain on…" CTAs ───────────────────────────────────────
  remainOnScribePlusCta: {
    defaultMessage: 'Continue on current plan',
    id: '/weYRmmtkm',
    description:
      'Secondary CTA on the Clinician trial-ended modal; closes the modal without upgrading (user stays on their current plan)',
  },
  remainOnScribeTeamCta: {
    defaultMessage: 'Continue on current plan',
    id: 'b2XiHnxslZ',
    description:
      'Secondary CTA on the Practice trial-ended modal for admins; closes the modal without upgrading (team stays on their current plan)',
  },

  // ── Existing contact-sales fallback (non-Stripe practices) ────────────
  talkToUsSecondary: {
    defaultMessage: 'Talk to us',
    id: '97HZWvwTz2',
    description: 'Secondary CTA on the Practice trial-ended modal; opens contact sales',
  },

  // ── Footer disclaimer + sales link ────────────────────────────────────
  disclaimerSoloClinician: {
    defaultMessage:
      "'Keep Evidence' opens Stripe to upgrade to Clinician. No changes to your billing if you 'Continue on current plan'.",
    id: 'Wtsa7I1GUH',
    description:
      'Footer disclaimer on the Clinician trial-ended modal explaining what each CTA does',
  },
  disclaimerPracticeAdmin: {
    defaultMessage:
      "'Keep Evidence' opens Stripe to upgrade to the new Practice plan. No changes to your billing if you 'Continue on current plan'.",
    id: 'qYcoBGnS1k',
    description:
      'Footer disclaimer on the Practice trial-ended modal for admins; the trailing "To talk to a Heidi sales expert, get in touch" sentence is rendered inline from `salesContactPrompt`',
  },
  salesContactPrompt: {
    defaultMessage: 'To talk to a Heidi sales expert, <link>get in touch</link>.',
    id: 'zQww1Irc6T',
    description:
      'Sub-line under the disclaimer on the Practice trial-ended modal for admins; <link> wraps the contact-sales link',
  },
  salesContactLink: {
    defaultMessage: 'get in touch',
    id: 'GAe+JDkyg4',
    description:
      'Inline link text inside the sales contact prompt on the Practice trial-ended modal',
  },

  // ── Tooltips for the "Answers in patient sessions" info icon ──────────
  evidenceAnswersTooltip: {
    defaultMessage:
      'Higher usage limits in patient sessions. Produce more clinical answers, summaries, handouts and care plans from Evidence.',
    id: 'FTEIfeJ28Z',
    description:
      'Tooltip next to Evidence answers on the trial-ended modal feature list; explains Clinician limits vs Evidence output',
  },
  evidenceAnswersTooltipAriaLabel: {
    defaultMessage: 'More about Evidence answers on Clinician',
    id: '0BLYOHgNBU',
    description:
      'Accessible label for the Evidence answers info control on the Clinician trial-ended modal',
  },

  // ── Tooltips for the "Set preferred sources" row ──────────────────────
  featureSetPreferredSourcesTooltip: {
    defaultMessage: 'Set what sources you want to inform your evidence-backed answers',
    id: 'DnRNJtxSis',
    description:
      'Tooltip next to the "Set preferred sources" row on the Clinician/Practice tab of the trial-ended modal; explains what choosing preferred sources actually does',
  },
  featureSetPreferredSourcesTooltipAriaLabel: {
    defaultMessage: 'More about preferred sources',
    id: 'X2i3jp/qbT',
    description:
      'Accessible label for the preferred-sources info control on the trial-ended modal feature list',
  },
  featureSetPreferredSourcesAvatarsTooltipIntro: {
    defaultMessage: 'Choose from sources such as:',
    id: 'bRvht53L78',
    description:
      'Intro line on the source-avatar composite tooltip; immediately precedes a bulleted list of preferred-source names',
  },
  featureSetPreferredSourcesAvatarsTooltipMore: {
    defaultMessage: 'And more.',
    id: '+uMAzPL9bZ',
    description:
      'Trailing line on the source-avatar composite tooltip; signals that the user has additional preferred sources beyond the three shown above',
  },
  featureSetPreferredSourcesAvatarsTooltipAriaLabel: {
    defaultMessage: 'Preferred sources available with this plan',
    id: 'uWQEYCrEPP',
    description:
      'Accessible label for the source-avatar stack on the "Set preferred sources" row of the trial-ended modal',
  },
});
